import { axiosConfig } from "@/services/axiosConfig";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
  getAccessToken,
  getTenantId,
  setLocalTenantId,
} from "@/lib/storeManager";
import { setAuth, type AuthUser } from "@/projects/lib/store";

function clearAuthTokens() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  const tenant = getTenantId();
  if (tenant) { setLocalTenantId(tenant); }
}

export type PortalRole = "admin" | "teacher";

export type GyanProfile = {
  id?: string | number;
  user_id?: string | number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  full_name?: string;
  user_type?: string;
  role?: string;
  teacher_name?: string;
  teacher_id?: string | number;
  mobile?: string;
  subject_department?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  is_superuser?: boolean;
  is_staff?: boolean;
  [key: string]: unknown;
};

type LoginResponse = {
  access?: string;
  refresh?: string;
  data?: {
    access?: string;
    refresh?: string;
  };
  user?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    user_type?: string;
  };
};

function unwrapData<T>(payload: T | { data: T }): T {
  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    (payload as { data: T }).data
  ) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

function extractTokens(response: LoginResponse) {
  const access = response.access ?? response.data?.access;
  const refresh = response.refresh ?? response.data?.refresh;
  return { access, refresh };
}

function normalizeRole(value?: string | boolean | null): PortalRole | null {
  if (value === true) { return "admin"; }
  if (typeof value !== "string") { return null; }
  const v = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (
    ["admin", "super_admin", "superadmin", "administrator"].includes(v)
  ) {
    return "admin";
  }
  if (["teacher", "faculty", "educator"].includes(v)) {
    return "teacher";
  }
  return null;
}

export function resolvePortalRole(profile: GyanProfile): PortalRole | null {
  return (
    normalizeRole(profile.user_type) ??
    normalizeRole(profile.role) ??
    (profile.is_superuser || profile.is_staff ? "admin" : null)
  );
}

export function profileToAuthUser(
  profile: GyanProfile,
  role: PortalRole,
  fallbackEmail: string
): AuthUser {
  const first = profile.first_name?.trim() ?? "";
  const last = profile.last_name?.trim() ?? "";
  const full =
    profile.teacher_name?.trim() ||
    profile.full_name?.trim() ||
    profile.name?.trim() ||
    [first, last].filter(Boolean).join(" ") ||
    profile.email ||
    fallbackEmail;

  return {
    role,
    id: String(
      profile.teacher_id ??
        profile.user_id ??
        profile.id ??
        profile.email ??
        fallbackEmail
    ),
    name: full,
    email: profile.email ?? fallbackEmail,
  };
}

function getErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: Record<string, unknown> };
    message?: string;
  };
  const data = err?.response?.data;
  if (!data) { return err?.message || fallback; }

  const candidates = [
    data.detail,
    data.message,
    data.error,
    data.non_field_errors,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) { return c; }
    if (Array.isArray(c) && typeof c[0] === "string") { return c[0]; }
  }
  return fallback;
}

/** POST /auth/login/ — stores access + refresh tokens */
export async function loginWithPassword(email: string, password: string) {
  try {
    const { data } = await axiosConfig.post<LoginResponse>("/auth/login/", {
      email,
      password,
    });
    const { access, refresh } = extractTokens(data ?? {});
    if (!access) {
      throw new Error("Login succeeded but no access token was returned.");
    }
    setLocalAccessToken(access);
    if (refresh) { setLocalRefreshToken(refresh); }
    if (data.user) {
      setLocalUserInfo(data.user);
    }
    return data;
  } catch (error) {
    throw new Error(
      getErrorMessage(error, "Invalid email or password. Please try again.")
    );
  }
}

/** GET /rajnagar_gyanmahostav/profile/ — call after login (and on session restore) */
export async function fetchMyProfile(): Promise<GyanProfile> {
  if (!getAccessToken()) {
    throw new Error("Not authenticated.");
  }
  try {
    const { data } = await axiosConfig.get<GyanProfile | { data: GyanProfile }>(
      "/rajnagar_gyanmahostav/profile/"
    );
    return unwrapData(data);
  } catch (error) {
    throw new Error(
      getErrorMessage(error, "Unable to load your profile. Please try again.")
    );
  }
}

/**
 * Full portal login: authenticate with password, then load profile for display.
 * Access is based on the access token (sent on subsequent APIs), not role.
 * `portal` selects which UI shell to enter after login.
 */
export async function loginPortal(
  email: string,
  password: string,
  portal: PortalRole = "admin"
): Promise<AuthUser> {
  await loginWithPassword(email.trim(), password);

  let profile: GyanProfile;
  try {
    profile = await fetchMyProfile();
  } catch (error) {
    clearAuthTokens();
    setAuth(null);
    throw error;
  }

  const authUser = profileToAuthUser(profile, portal, email.trim());
  setAuth(authUser);
  setLocalUserInfo({
    first_name: profile.first_name,
    last_name: profile.last_name,
    email: authUser.email,
    user_type: profile.user_type ?? profile.role ?? portal,
    ...profile,
  });
  return authUser;
}

/**
 * Refresh display session from profile API when a token already exists.
 * Does not enforce role — portal layout is chosen by the route the user is on.
 */
export async function refreshSessionFromProfile(
  portal: PortalRole = "admin"
): Promise<AuthUser | null> {
  if (!getAccessToken()) {
    return null;
  }
  const profile = await fetchMyProfile();
  const authUser = profileToAuthUser(
    profile,
    portal,
    String(profile.email ?? "")
  );
  setAuth(authUser);
  return authUser;
}
